import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { NAV_ITEMS, canSee } from "@/lib/nav";
import { PageHeader, Kpi, ListRow, inr } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { ChangePasswordCard } from "@/components/ChangePasswordCard";
import { LogOut, Mail, Users, Contact, CheckCircle2, IndianRupee, CalendarCheck, Wallet, BookOpenCheck, Banknote, PlaneTakeoff } from "lucide-react";

export function ProfilePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [slips, setSlips] = useState([]);
  useEffect(() => { if (user?.role !== "parent") api.get("/payroll/my").then((r) => setSlips(r.data)).catch(() => {}); }, [user]);
  return (
    <AppShell>
      <PageHeader eyebrow="Profile" title={user?.name} subtitle={ROLE_LABELS[user?.role]} />
      <div className="bg-white rounded-2xl shadow-sm p-5 space-y-2 max-w-xl">
        <div className="flex items-center gap-2 text-sm text-slate-700"><Mail size={15} /> {user?.email}</div>
        {user?.class_name && <div className="text-sm text-slate-700">Class: {user.class_name} - {user.section}</div>}
      </div>
      <ChangePasswordCard />
      {slips.length > 0 && (
        <div className="mt-5 max-w-xl">
          <h2 className="font-display font-bold text-slate-900 mb-2">My payslips</h2>
          <div className="bg-white rounded-2xl shadow-sm divide-y divide-slate-100">
            {slips.map((p) => <div key={p.id} className="flex justify-between px-4 py-3 text-sm"><span>{p.month}</span><span className="font-semibold">{inr(p.net)} • {p.status}</span></div>)}
          </div>
        </div>
      )}
      <div className="mt-5 max-w-xl grid gap-2">
        <Link to="/leave" data-testid="profile-leave-link" className="bg-white rounded-2xl shadow-sm px-4 py-3 text-sm font-semibold text-slate-800 flex items-center gap-2"><PlaneTakeoff size={16} /> Leave requests</Link>
        <Button data-testid="profile-logout" variant="outline" onClick={() => { logout(); navigate("/login"); }} className="text-rose-600 border-rose-200"><LogOut size={16} className="mr-2" /> Sign out</Button>
      </div>
    </AppShell>
  );
}

export function MorePage() {
  const { user } = useAuth();
  const items = NAV_ITEMS.filter((i) => canSee(i, user?.role) && i.to !== "/dashboard");
  return (
    <AppShell>
      <PageHeader eyebrow="Menu" title="More" />
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {items.map((i) => (
          <Link key={i.to} to={i.to} data-testid={`more-${i.to.slice(1)}`} className="bg-white rounded-2xl shadow-sm p-4 flex flex-col items-center gap-2 text-center">
            <div className="h-10 w-10 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center"><i.icon size={20} /></div>
            <span className="text-xs font-semibold text-slate-800">{i.label}</span>
          </Link>
        ))}
      </div>
    </AppShell>
  );
}

export function ReportsPage() {
  const { user } = useAuth();
  const [s, setS] = useState({});
  useEffect(() => { api.get("/home/summary").then((r) => setS(r.data)).catch(() => {}); }, []);
  const r = user?.role;
  return (
    <AppShell>
      <PageHeader eyebrow="Insights" title="Reports & Analytics" subtitle="Key numbers and detailed reports." />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <Kpi testid="report-students" icon={Users} value={s.students ?? "—"} label="Students" tone="purple" />
        <Kpi testid="report-staff" icon={Contact} value={s.staff ?? "—"} label="Staff" tone="blue" />
        <Kpi testid="report-attendance" icon={CheckCircle2} value={s.attendance_pct == null ? "—" : `${s.attendance_pct}%`} label="Attendance today" tone="green" />
        <Kpi testid="report-pending" icon={IndianRupee} value={inr(s.fees_pending)} label="Fees pending" tone="red" />
      </div>
      <div className="space-y-2.5 max-w-2xl">
        {r !== "accountant" && r !== "hr_manager" && <ListRow testid="report-row-attendance" to="/attendance" icon={CalendarCheck} label="Monthly attendance report" />}
        {["master_admin", "super_admin", "principal", "accountant"].includes(r) && <ListRow testid="report-row-fees" to="/fees" icon={Wallet} label="Fee collection & dues" color="orange" />}
        {r !== "accountant" && r !== "hr_manager" && <ListRow testid="report-row-grades" to="/gradebook" icon={BookOpenCheck} label="Student progress (grades)" color="green" />}
        {["master_admin", "super_admin", "hr_manager"].includes(r) && <ListRow testid="report-row-payroll" to="/payroll" icon={Banknote} label="Payroll summary" color="blue" />}
      </div>
    </AppShell>
  );
}
